import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';
import {
  FaHourglassStart, FaHourglassHalf, FaHourglassEnd, FaClock, FaCircle, FaFlag, FaFolder,
} from 'react-icons/fa';
import { WhiteCircleIcon } from '../UI';
import * as C from '../../theme/colors.constants';

/**
 * Ícones de alerta da grid de processos.
 *
 * Porte direto de `abe-web-react/src/components/UI/AlertasProcesso/AlertasProcesso.jsx`.
 * Os mapas de cor e os ícones são os mesmos do projeto real — só os valores
 * hexadecimais foram trocados pelos da nova paleta (mesma semântica: vermelho
 * vencido / laranja no dia / verde futuro).
 */

const ICONE_FASE = {
  V: FaHourglassStart, // no prazo
  L: FaHourglassHalf, // no limite
  M: FaHourglassEnd, // vencido
};

/** Ampulheta: dias do processo com o negociador / vencimento na fase. */
export function AlertaVencimentoProcesso({ cor, dias }) {
  if (!cor || cor === 'E') return <Box sx={{ width: 20, height: 20 }} />;
  const Icone = ICONE_FASE[cor] || FaHourglassHalf;
  return (
    <Tooltip title={`Dias do processo com o negociador: ${dias ?? 0}`} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <WhiteCircleIcon>
          <Icone size={11} color={C.alertaFaseColor[cor]} />
        </WhiteCircleIcon>
        <Box component="span" sx={{ fontSize: 'clamp(11px, 0.62vw, 14px)', color: C.black }}>
          {dias ?? 0}
        </Box>
      </Box>
    </Tooltip>
  );
}
AlertaVencimentoProcesso.propTypes = { cor: PropTypes.string, dias: PropTypes.number };
AlertaVencimentoProcesso.defaultProps = { cor: null, dias: null };

/** Relógio: compromisso/agendamento. */
export function AlertaCompromissoProcesso({ compromisso, hint }) {
  if (!compromisso) return <Box sx={{ width: 20, height: 20 }} />;
  const cor = C.compromissoColor[compromisso] || C.compromissoColor.DISABLED;
  const textos = {
    EXPIRADO: 'Compromisso expirado',
    EXPIRACAO_PROXIMA: 'Compromisso expira hoje',
    MARCADO: 'Compromisso marcado',
    DISABLED: 'Sem compromisso',
  };
  return (
    <Tooltip title={hint ? `${textos[compromisso]} — ${hint}` : textos[compromisso]} arrow>
      <WhiteCircleIcon>
        <FaClock size={12} color={cor} />
      </WhiteCircleIcon>
    </Tooltip>
  );
}
AlertaCompromissoProcesso.propTypes = { compromisso: PropTypes.string, hint: PropTypes.string };
AlertaCompromissoProcesso.defaultProps = { compromisso: null, hint: null };

/** Bolinha roxa: processo não trabalhado. */
export function AlertaProcessoNaoTrabalhado({ ativo }) {
  if (!ativo) return <Box sx={{ width: 20, height: 20 }} />;
  return (
    <Tooltip title="Processo não trabalhado" arrow>
      <WhiteCircleIcon>
        <FaCircle size={8} color={C.purple} />
      </WhiteCircleIcon>
    </Tooltip>
  );
}
AlertaProcessoNaoTrabalhado.propTypes = { ativo: PropTypes.bool };
AlertaProcessoNaoTrabalhado.defaultProps = { ativo: false };

/** Bandeira: processo com requisição. */
export function AlertaComRequisicao({ hint }) {
  if (!hint) return <Box sx={{ width: 20, height: 20 }} />;
  return (
    <Tooltip title={hint} arrow>
      <WhiteCircleIcon>
        <FaFlag size={10} color={C.tangerine} />
      </WhiteCircleIcon>
    </Tooltip>
  );
}
AlertaComRequisicao.propTypes = { hint: PropTypes.string };
AlertaComRequisicao.defaultProps = { hint: null };

/** Pasta. */
export function AlertaPasta({ pasta }) {
  if (!pasta) return <Box sx={{ width: 20, height: 20 }} />;
  return (
    <Tooltip title={pasta} arrow>
      <WhiteCircleIcon>
        <FaFolder size={10} color={C.navy} />
      </WhiteCircleIcon>
    </Tooltip>
  );
}
AlertaPasta.propTypes = { pasta: PropTypes.string };
AlertaPasta.defaultProps = { pasta: null };

/** Cor do texto da coluna "Situação", conforme a ordem de exibição pedida. */
export const corSituacao = {
  PRAZO_VENCIDO: C.tomato,
  PRAZO_DIA: C.tangerine,
  PRAZO_FUTURO: C.midGreen,
  NAO_TRABALHADO: C.purple,
  SEM_PRAZO: C.warmGrey,
};
